import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import { syncServerTime, msUntil, formatCountdown } from '../../common/clock';

/**
 * Survives across mounts of the card itself.
 *
 * IndexPage is fully remounted on every route change (Flarum keys its root
 * vnode by route, per DefaultResolver), so returning from a discussion to the
 * home page tears down and recreates ContestCard. Without a cache outside the
 * instance, the card would blank out and refetch on every such visit even
 * though the featured contest rarely changes between polls.
 */
let lastKnownContest = null;
let lastKnownAt = 0;

// Older than this, the cached card is not shown while a fresh copy loads —
// a visitor returning after a long absence should not see stale data.
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * The compact card above the discussion list.
 *
 * Renders nothing at all unless there is a published contest that is due to be
 * shown, so an idle forum looks exactly as it did before the extension.
 * Polls slowly; the contest page itself does the fast polling.
 */
export default class ContestCard extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    // Paint the last known state immediately so a remount (returning from a
    // discussion, for instance) does not flash the card away while the
    // request that will very likely return the same thing is in flight.
    const fresh = lastKnownContest && Date.now() - lastKnownAt < CACHE_MAX_AGE_MS;

    this.contest = fresh ? lastKnownContest : null;
    this.loaded = fresh;
    this.timer = null;

    this.onVisibilityChange = () => {
      if (!document.hidden) this.load();
    };

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.load();

    // Redraws the countdown between fetches.
    this.ticker = setInterval(() => {
      if (!document.hidden && this.contest) m.redraw();
    }, 1000);
  }

  onremove(vnode) {
    super.onremove(vnode);

    clearInterval(this.ticker);
    if (this.timer) clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  load() {
    return app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests`,
        params: { featured: 1 },
      })
      .then((response) => {
        this.contest = response.data || null;
        this.loaded = true;

        // Shared across mounts, so the next time this card is created it can
        // paint straight away instead of starting blank.
        lastKnownContest = this.contest;
        lastKnownAt = Date.now();

        if (this.contest) {
          syncServerTime(this.contest.state?.serverNow);
        }

        this.schedule();
        m.redraw();
      })
      .catch(() => {
        // A failed refresh does not evict a still-fresh cache: what was on
        // screen a moment ago is still a better guess than nothing.
        this.loaded = true;
        this.schedule();
      });
  }

  /**
   * Home-page polling is deliberately infrequent; a visitor reading the
   * discussion list does not need second-level accuracy.
   */
  schedule() {
    if (this.timer) clearTimeout(this.timer);

    const configured = parseInt(app.forum.attribute('happyQuizPollCard'), 10);
    const base = (Number.isFinite(configured) && configured > 0 ? configured : 120) * 1000;

    const delay = document.hidden ? base * 3 : base;

    this.timer = setTimeout(() => this.load(), delay + Math.floor(Math.random() * 5000));
  }

  view() {
    if (!this.loaded || !this.contest) return null;

    const contest = this.contest;
    const phase = contest.state.phase;

    // A finished contest with final results celebrates its winners instead of
    // repeating the compact row.
    if ((phase === 'completed' || phase === 'finalizing') && contest.winners?.length) {
      return this.winnersView(contest);
    }

    const info = this.infoFor(contest, phase);

    if (!info) return null;

    return (
      <div className="HappyQuizCard">
        <div className="HappyQuizCard-body">
          <div className="HappyQuizCard-eyebrow">{app.translator.trans('happydigital-quiz.forum.card.eyebrow')}</div>
          <div className="HappyQuizCard-title">{contest.title}</div>
          <div className="HappyQuizCard-meta">
            {/* The countdown carries its own weight instead of sitting inside
                the sentence, so the card reads at a glance. */}
            {info.countdown ? (
              <span
                className={'HappyQuizCard-countdown' + (info.urgent ? ' is-urgent' : '')}
                role="timer"
                aria-live="off"
              >
                {info.countdown}
              </span>
            ) : null}
            {info.meta}
          </div>
        </div>
        <div className="HappyQuizCard-action">
          <Link className="Button Button--primary" href={app.route('happyQuiz.show', { slug: contest.slug })}>
            {info.action}
          </Link>
        </div>
      </div>
    );
  }

  /**
   * The celebration card: what the contest was, and who won it.
   *
   * Shown for a configured window after the results are finalised, then the
   * card falls back to whatever is next.
   */
  winnersView(contest) {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.card.${key}`, params);

    const byRank = (rank) => contest.winners.find((w) => w.rank === rank);

    // Visual order, not ranking order: the winner belongs in the middle.
    const steps = [byRank(2), byRank(1), byRank(3)];

    return (
      <div className="HappyQuizWinners">
        <img
          className="HappyQuizWinners-confetti"
          src={`${app.forum.attribute('assetsBaseUrl')}/extensions/happydigital-quiz/confett.png`}
          alt=""
        />

        <div className="HappyQuizWinners-content">
          <div className="HappyQuizWinners-info">
            <div className="HappyQuizWinners-label">{t('eyebrow')}</div>
            <div className="HappyQuizWinners-title">{contest.title}</div>
            <div className="HappyQuizWinners-message">{t('congratulations')}</div>

            {/* The action sits under the text rather than off to the right. */}
            <Link
              className="HappyQuizWinners-button"
              href={app.route('happyQuiz.show', { slug: contest.slug })}
            >
              {t('see_results')}
            </Link>
          </div>

          <div className="HappyQuizWinners-podium">
            {steps.map((winner, i) => {
              if (!winner) return null;

              const place = winner.rank;

              const body = [
                <span className="HappyQuizWinners-frame">
                  <span className="HappyQuizWinners-avatar">
                    {winner.avatarUrl ? (
                      <img src={winner.avatarUrl} alt="" />
                    ) : (
                      (winner.username || '?').charAt(0).toLocaleUpperCase()
                    )}
                  </span>
                  <img
                    className="HappyQuizWinners-ring"
                    src={`${app.forum.attribute('assetsBaseUrl')}/extensions/happydigital-quiz/frame-${place}.png`}
                    alt=""
                  />
                </span>,
                <span className="HappyQuizWinners-name" title={winner.username}>
                  {winner.username}
                </span>,
              ];

              return (
                <div className={`HappyQuizWinners-winner is-place${place}`}>
                  {/* A deleted user has no slug, so it stays unlinked rather
                      than pointing at a profile that cannot load. */}
                  {winner.userSlug ? (
                    <Link className="HappyQuizWinners-link" href={app.route('user', { username: winner.userSlug })}>
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  infoFor(contest, phase) {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.card.${key}`, params);

    switch (phase) {
      case 'scheduled': {
        // A live countdown reads more urgently than a fixed date, and stays
        // correct for a viewer in any timezone.
        const remaining = msUntil(contest.state.startsAt);

        return {
          countdown: formatCountdown(remaining),
          // Under an hour the start is imminent enough to highlight.
          urgent: remaining < 3600000,
          meta: contest.prizes.length ? t('upcoming_suffix') : t('upcoming_suffix_no_prize'),
          action: t('review'),
        };
      }

      case 'waiting':
        // The lobby is open, so the start is always close: always urgent.
        return {
          countdown: formatCountdown(msUntil(contest.state.startsAt)),
          urgent: true,
          meta: t('lobby_suffix'),
          action: t('join'),
        };

      case 'live':
        return {
          meta: t('live', {
            current: (contest.state.questionIndex ?? 0) + 1,
            total: contest.questionCount,
          }),
          // A registered participant is going back to their run; everyone
          // else is watching.
          action: contest.isRegistered ? t('back_to_quiz') : t('watch'),
        };

      case 'finalizing':
      case 'completed':
        return {
          meta: t('completed'),
          action: t('see_results'),
        };

      default:
        // Drafts and cancelled contests never surface here.
        return null;
    }
  }
}
